import { useContext, useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { AuthContext } from "~/utils/useAuth";
import { styled, keyframes, vh, darkTheme } from "~/ui/style/stitches.config";
import { updateJournalEntry, interpretJournalEntry, JournalEntry } from "~/api/journalApi";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { isIOSNativeApp, isIOSAppVersionAtMost } from "~/utils/deviceDetection";
import { motion } from "framer-motion";
import { FeatureFeedbackForm } from "~/ui/components/FeatureFeedbackForm";
import { trackEvent } from "~/api/analyticsApi";
import { CopyButton } from "~/ui/components/CopyButton";
import { hasFullAccess } from "~/api/userApi";

const fadeIn = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 }
});

const fadeOut = keyframes({
  '0%': { opacity: 1, transform: 'scale(1)' },
  '100%': { opacity: 0, transform: 'scale(0.95)' }
});

const deckFolderToName: { [key: string]: string } = {
  'dirt': 'Summer of Bibliomancy',
  'viator-somniorum': 'Viator Somniorum',
  'internet-lore': 'Internet Lore Tarot',
  'mixed-signals': 'Mixed Signals',
  'mushroom': 'Mushroom Tarot',
  'gothmancy': 'Gothmancy Tarot',
  'black': 'The Black Deck',
  'eldritch': 'Eldritch Overload',
  'rider-waite': '',
  'marseille': '',
};

const getDeckNameFromPath = (imagePath: string) => {
  if (!imagePath?.includes('/decks/')) return '';
  
  const folderName = imagePath.split('/decks/')[1]?.split('/')[0];
  return deckFolderToName[folderName] || '';
};

interface JournalOverlayProps {
  entry: JournalEntry;
  onClose: () => void;
  onSave: (updatedContent: any) => void;
}

export const JournalOverlay = ({ entry, onClose, onSave }: JournalOverlayProps) => {
  const { appDarkMode: dk } = useSnapshot(localState);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(entry.content);
  const [isSaving, setIsSaving] = useState(false);
  const [isInterpreting, setIsInterpreting] = useState(false);
  const [showPositionInput, setShowPositionInput] = useState<{[key: number]: boolean}>({});
  const { user } = useContext(AuthContext);

  // Inline reflections state (for manual-only entries)
  const [inlineReflections, setInlineReflections] = useState(entry.content?.reflections || '');
  const [showReflectionsButtons, setShowReflectionsButtons] = useState(false);
  const [isSavingReflections, setIsSavingReflections] = useState(false);

  useEffect(() => {
    // Track when overlay opens with entry ID
    trackEvent("/journal/open_entry", { entryId: entry.id });
    
    // Prevent body scroll when overlay is open
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  const handleClose = () => {
    // Cancel any pending operations
    setIsEditing(false);
    setShowPositionInput({});
    onClose();
  };

  const handleSave = async () => {
    // Check if there are any changes
    if (JSON.stringify(editedContent) === JSON.stringify(entry.content)) {
      setIsEditing(false);
      return;
    }

    trackEvent("/journal/save_entry");
    setIsSaving(true);
    try {
      await onSave(editedContent);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(entry.content);
    setIsEditing(false);
    setShowPositionInput({});
  };

  const handleSaveInlineReflections = async () => {
    trackEvent("/journal/save_reflections");
    setIsSavingReflections(true);
    try {
      const updatedContent = { ...editedContent, reflections: inlineReflections.trim() || '' };
      setEditedContent(updatedContent);
      await onSave(updatedContent);
      setShowReflectionsButtons(false);
    } catch (error) {
      console.error('Failed to save reflections:', error);
    } finally {
      setIsSavingReflections(false);
    }
  };

  const handleCancelInlineReflections = () => {
    setInlineReflections(editedContent?.reflections || '');
    setShowReflectionsButtons(false);
  };

  const isManualOnly = editedContent && (
    !editedContent.overall ||
    !editedContent.gist ||
    editedContent.cards?.every((card: any) => !card.interpretation)
  );

  const handleInterpret = async () => {
    if (!hasFullAccess(user)) {
      localStorage.setItem('postPaymentRedirect', `/journal/${entry.id}`);
      window.location.href = '/plans';
      return;
    }

    try {
      setIsInterpreting(true);
      trackEvent("/journal/interpret_entry");
      const updatedEntry = await interpretJournalEntry(entry.id);
      setEditedContent(updatedEntry.content);
      await onSave(updatedEntry.content);

      // Scroll to the analysis section after interpretation
      setTimeout(() => {
        const dateDivider = document.querySelector('.summary-date-divider');
        if (dateDivider) {
          (dateDivider as HTMLElement).style.scrollMarginTop = '80px';
          dateDivider.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 150);
    } catch (error) {
      console.error('Failed to interpret entry:', error);
    } finally {
      setIsInterpreting(false);
    }
  };

  const updateField = (field: string, value: string) => {
    setEditedContent((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const updateCardField = (cardIndex: number, field: string, value: string) => {
    setEditedContent((prev: any) => ({
      ...prev,
      cards: prev.cards.map((card: any, index: number) => 
        index === cardIndex ? { ...card, [field]: value } : card
      )
    }));
  };

  const updateCardPosition = (cardIndex: number, position: string) => {
    setEditedContent((prev: any) => ({
      ...prev,
      cards: prev.cards.map((card: any, index: number) => 
        index === cardIndex ? { ...card, position: position || null } : card
      )
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTextareaStyle = (minHeight: string) => ({
    width: '100%',
    minHeight,
    backgroundColor: dk ? '#000000' : '#fff',
    border: dk ? '1px solid #505050' : '1px solid #ddd',
    borderRadius: '12px',
    padding: '12px',
    color: dk ? '#fff' : '#000',
    fontSize: '13px',
    lineHeight: '1.4',
    resize: 'none' as const,
    outline: 'none',
    overflow: 'hidden' as const,
    transition: 'border-color 0.2s ease, height 0.3s ease',
    userSelect: 'text' as const,
    WebkitUserSelect: 'text' as const
  });

  const handleTextareaFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.style.borderColor = dk ? '#aaaaaa' : '#888888';
  };

  const handleTextareaBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.target.style.borderColor = dk ? '#505050' : '#ddd';
  };

  const handleTextareaInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    target.style.height = 'auto';
    target.style.height = target.scrollHeight + 'px';
  };

  const handleTextareaFocusWithExpand = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    handleTextareaFocus(e);
    // Auto-expand on focus
    const target = e.target as HTMLTextAreaElement;
    target.style.height = 'auto';
    target.style.height = target.scrollHeight + 'px';
  };

  const getCardKeywords = (card: any) => {
    // Look up keywords from originalData
    if (entry.originalData?.cards) {
      const originalCard = entry.originalData.cards.find((origCard: any) => 
        origCard.cardName === card.name.replace(/ \(Reversed\)| Reversed/g, '')
      );
      return originalCard?.keywords || [];
    }
    return [];
  };

  return (
    <StyledSummaryOverlay
      Theme={dk ? "dark" : "light"}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className="summary-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="sticky-header"
          style={{
            position: 'sticky',
            top: '0px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '0px 10px 30px 10px',  
            zIndex: 4,
            width: 'calc(100% + 64px)',
            marginLeft: '-32px',
            marginRight: '-32px',
            boxSizing: 'border-box',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px'
          }}>
          
          <img
            className="x-button"
            onClick={handleClose}
            src="/images/exit.svg"
            style={{
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              opacity: 1,
              transition: 'opacity 0.2s ease',
            }}
            onMouseOver={e => e.currentTarget.style.opacity = '0.6'}
            onMouseOut={e => e.currentTarget.style.opacity = '1'}
          />
        </div>

        {/* Show query + date + reflections above card preview */}
        {isManualOnly && (
          <div style={{
            width: '100%',
            opacity: 0,
            animation: `${fadeIn} 0.5s ease forwards`,
            animationDelay: '0.12s'
          }}>
            <div
              className="summary-date-divider"
              style={{
                position: 'relative',
                width: '96%',
                margin: '0px auto 34px auto',
                textAlign: 'center'
              }}
            >
              <div style={{
                width: '100%',
                height: '1px',
                backgroundColor: 'var(--colors-gray400)',
              }} />
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: dk ? 'var(--colors-darkwash)' : '#f6f6f6',
                padding: '0 12px',
                fontSize: '10px',
                color: dk ? '#bbbbbb' : 'rgb(91, 91, 91)',
                userSelect: 'text',
                WebkitUserSelect: 'text',
              }}>
                {formatDate(entry.createdAt)}
              </div>
            </div>

            <StyledAnalysis Theme={dk ? "dark" : "light"}>
              {editedContent?.query && (
                <div className="query" style={{ marginBottom: '20px' }}>
                  <p>{editedContent.query}</p>
                </div>
              )}
              <div className="reflections" style={{ marginBottom: '30px' }}>
                <h2 style={{ marginTop: '0.4rem' }}>My Reflections</h2>
                <textarea
                  value={inlineReflections}
                  onChange={(e) => {
                    setInlineReflections(e.target.value);
                    if (!showReflectionsButtons) setShowReflectionsButtons(true);
                  }}
                  placeholder="What's jumping out to you?"
                  style={getTextareaStyle('80px')}
                  onFocus={handleTextareaFocusWithExpand}
                  onBlur={handleTextareaBlur}
                  onInput={handleTextareaInput}
                  maxLength={500}
                />
                {showReflectionsButtons && (
                  <div style={{
                    display: 'flex',
                    gap: '6px',
                    justifyContent: 'right',
                    marginTop: '4px',
                    height: '30px',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={handleCancelInlineReflections}
                      style={{
                        padding: '7px',
                        borderRadius: '100px',
                        border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                        backgroundColor: dk ? '#000000' : '#fff',
                        color: dk ? '#fff' : '#000',
                        fontSize: '12px',
                        cursor: 'pointer',
                        width: '84px',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = dk ? '#404040' : '#ebebeb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = dk ? '#000000' : '#fff';
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveInlineReflections}
                      disabled={isSavingReflections}
                      style={{
                        padding: '7px',
                        borderRadius: '100px',
                        border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                        backgroundColor: dk ? '#000000' : '#fff',
                        color: dk ? '#fff' : '#000',
                        fontSize: '12px',
                        cursor: 'pointer',
                        width: '84px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s ease',
                        ...(isSavingReflections && { opacity: 0.7 })
                      }}
                      onMouseEnter={(e) => {
                        if (!isSavingReflections) e.currentTarget.style.backgroundColor = dk ? '#404040' : '#ebebeb';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSavingReflections) e.currentTarget.style.backgroundColor = dk ? '#000000' : '#fff';
                      }}
                    >
                      {isSavingReflections ? (
                        <SimpleLoadingIndicator
                          style={{
                            width: '12px',
                            height: '12px',
                            position: 'static',
                            opacity: 0.7,
                            margin: '1px'
                          }}
                        />
                      ) : (
                        'Save'
                      )}
                    </button>
                  </div>
                )}
              </div>
            </StyledAnalysis>
          </div>
        )}

        {/* Card preview section */}
        <div className="card-previews" style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          {editedContent?.cards?.map((card: any, index: number) => (
            <div key={index} className="card-preview-item" style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              width: '80px' 
            }}>
              <div style={{
                transform: `rotate(${(card.name.includes('Reversed') || card.name.includes('(Reversed)')) ? '180deg' : '0deg'})`,
                width: '100%'
              }}>
                <div style={{
                  position: 'relative',
                  borderRadius: '6px'
                }}>
                  <img 
                    src={card.imagePath}
                    alt={card.name}
                    style={{
                      width: '100%', 
                      height: '100%',
                      display: 'block',
                      borderRadius: '6px'
                    }}
                  />
                </div>
              </div>
              <div style={{
                fontSize: '11px', 
                textAlign: 'center',
                textTransform: 'uppercase',
                color: dk ? '#fff' : '#000'
              }}>
                {card.name.replace(/ \(Reversed\)| Reversed/g, '')}
                {(card.name.includes('Reversed') || card.name.includes('(Reversed)')) && ' (R)'}
                {card.position && (
                  <div style={{
                    fontSize: '10px', 
                    marginTop: '2px',
                    textTransform: 'none',
                    opacity: 0.5,
                    color: dk ? '#fff' : '#000'
                  }}>
                    {card.position}
                  </div>
                )}
                {/* Keywords section */}
                <div style={{
                  fontSize: '9px',
                  textAlign: 'center',
                  fontWeight: '300',
                  lineHeight: '.9rem',
                  marginTop: '7px',
                  textTransform: 'lowercase',
                  opacity: 0.9,
                  color: dk ? '#fff' : '#000',
                  letterSpacing: '.01em',
                }}>
                  {getCardKeywords(card)?.join(' • ')}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Deck name below all cards */}
        {editedContent?.cards?.length > 0 && getDeckNameFromPath(editedContent.cards[0].imagePath) && (
          <div style={{
            fontSize: '9px',
            color: '#8c8c8c',
            letterSpacing: '.02em',
            textTransform: 'capitalize',
          }}>Deck:&nbsp;
            {getDeckNameFromPath(editedContent.cards[0].imagePath)}
          </div>
        )}

        <div className="summary-content" style={{
          flexDirection: 'column',
          opacity: 0,
          animation: `${fadeIn} 0.5s ease forwards`,
          animationDelay: '0.12s'
        }}>
          {/* Date divider - only show here for interpreted entries (manual-only shows it above cards) */}
          {!isManualOnly && (
          <div
            className="summary-date-divider"
            style={{
              position: 'relative',
              width: '96%',
              margin: '30px auto 50px auto',
              textAlign: 'center'
            }}
          >
            {/* Horizontal line */}
            <div style={{
              width: '100%',
              height: '1px',
              backgroundColor: 'var(--colors-gray400)',
            }} />

            {/* Floating date label */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: dk ? 'var(--colors-darkwash)' : '#f6f6f6',
              padding: '0 12px',
              fontSize: '10px',
              color: dk ? '#bbbbbb' : 'rgb(91, 91, 91)',
              userSelect: 'text',
              WebkitUserSelect: 'text',
            }}>
              {formatDate(entry.createdAt)}
            </div>
          </div>
          )}

          <StyledAnalysis Theme={dk ? "dark" : "light"}>
            {/* Query section - only show here for interpreted entries (manual-only shows it above cards) */}
            {!isManualOnly && (editedContent?.query || isEditing) && (
              <div style={{ marginBottom: '30px' }}>
                {isEditing && (
                  <h2 style={{ 
                    marginBottom: '12px', 
                    fontSize: '27px',
                    fontFamily: 'KeplerLightCondensed',
                    marginTop: '.5rem',
                    fontWeight: 100,
                    scrollMarginTop: '60px'
                  }}>Your question</h2>
                )}
                {isEditing ? (
                  <textarea
                    value={editedContent?.query || ''}
                    onChange={(e) => updateField('query', e.target.value)}
                    placeholder="What are you exploring?"
                    style={getTextareaStyle('60px')}
                    onFocus={handleTextareaFocusWithExpand}
                    onBlur={handleTextareaBlur}
                    onInput={handleTextareaInput}
                    maxLength={300}
                  />
                ) : (
                  <div className="query">
                    <p>{editedContent?.query}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Cards section */}
            {editedContent?.cards && editedContent.cards.length > 0 && !isManualOnly && (
              <div style={{ marginBottom: '30px' }}>
                {editedContent.cards.map((card: any, index: number) => (
                  <div key={index} style={{ marginBottom: '30px' }}>
                    <div className="card-section">
                      <img
                        src={card.imagePath}
                        alt={card.name}
                        style={{
                          transform: (card.name.includes('Reversed') || card.name.includes('(Reversed)')) ? 'rotate(180deg)' : 'none'
                        }}
                      />
                      <div className="card-content" style={{ width: '100%' }}>
                        <h1>
                          {card.name}
                          {!isEditing && card.position && (
                            <span style={{ 
                              color: dk ? '#fff' : '#000',
                              fontFamily: 'KeplerLightCondensed',
                              fontWeight: 100
                            }}>
                              {' '}({card.position})
                            </span>
                          )}
                        </h1>
                        
                        {isEditing && (
                          <>
                            {/* Spread position section */}
                            {showPositionInput[index] || card.position ? (
                              <div style={{ 
                                position: 'relative',
                                marginBottom: '6px'
                              }}>
                                <input
                                  type="text"
                                  value={card.position || ''}
                                  onChange={(e) => updateCardPosition(index, e.target.value)}
                                  placeholder="Slot name"
                                  maxLength={30}
                                  style={{
                                    width: '100%',
                                    backgroundColor: dk ? '#000000' : '#fff',
                                    border: dk ? '1px solid #505050' : '1px solid #ddd',
                                    borderRadius: '12px',
                                    padding: '6px 24px 6px 12px',
                                    color: dk ? '#fff' : '#000',
                                    fontSize: '11px',
                                    lineHeight: '1.4',
                                    outline: 'none',
                                    transition: 'border-color 0.2s ease'
                                  }}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = dk ? '#aaaaaa' : '#888888';
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = dk ? '#505050' : '#ddd';
                                  }}
                                  autoFocus={showPositionInput[index]}
                                />
                                <button
                                  onClick={() => {
                                    setShowPositionInput(prev => ({...prev, [index]: false}));
                                    updateCardPosition(index, '');
                                  }}
                                  style={{
                                    position: 'absolute',
                                    right: '6px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    color: dk ? '#fff' : '#000',
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    opacity: 0.6,
                                    padding: '2px',
                                    fontWeight: '300',
                                    lineHeight: 1
                                  }}
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setShowPositionInput(prev => ({...prev, [index]: true}))}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: dk ? '#fff' : '#000',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  opacity: 0.5,
                                  padding: '0',
                                  marginBottom: '12px',
                                  textAlign: 'left'
                                }}
                              >
                                + spread slot
                              </button>
                            )}
                          </>
                        )}

                        {isEditing ? (
                          <textarea
                            value={card.interpretation || ''}
                            onChange={(e) => updateCardField(index, 'interpretation', e.target.value)}
                            placeholder="Your take..."
                            style={getTextareaStyle('100px')}
                            onFocus={handleTextareaFocusWithExpand}
                            onBlur={handleTextareaBlur}
                            onInput={handleTextareaInput}
                            maxLength={2000}
                          />
                        ) : (
                          <p>{card.interpretation}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Overall section */}
            {(editedContent?.overall || isEditing) && (
              <div style={{ marginBottom: '40px' }}>
                <h2 style={{ 
                  fontSize: '27px',
                  fontFamily: 'KeplerLightCondensed',
                  marginTop: '2.2rem',
                  marginBottom: '0.5rem',
                  fontWeight: 100
                }}>Overall</h2>
                {isEditing ? (
                  <textarea
                    value={editedContent?.overall || ''}
                    onChange={(e) => updateField('overall', e.target.value)}
                    placeholder="What's the overall message?"
                    style={getTextareaStyle('120px')}
                    onFocus={handleTextareaFocusWithExpand}
                    onBlur={handleTextareaBlur}
                    onInput={handleTextareaInput}
                    maxLength={2000}
                  />
                ) : (
                  <div className="overall">
                    <p>{editedContent?.overall}</p>
                  </div>
                )}
              </div>
            )}

            {/* Takeaway section */}
            {(editedContent?.gist || isEditing) && (
              <div style={{ marginBottom: '30px' }}>
                <h2 style={{ 
                  fontSize: '27px',
                  fontFamily: 'KeplerLightCondensed',
                  marginTop: '2.2rem',
                  marginBottom: '0.5rem',
                  fontWeight: 100
                }}>Takeaway</h2>
                {isEditing ? (
                  <textarea
                    value={editedContent?.gist || ''}
                    onChange={(e) => updateField('gist', e.target.value)}
                    placeholder="What's the big advice?"
                    style={getTextareaStyle('80px')}
                    onFocus={handleTextareaFocusWithExpand}
                    onBlur={handleTextareaBlur}
                    onInput={handleTextareaInput}
                    maxLength={250}
                  />
                ) : (
                  <div className="takeaway">
                    <p>{editedContent.gist}</p>
                  </div>
                )}
              </div>
            )}

            {/* Ask Yourself section */}
            {(editedContent?.question || isEditing) && (
              <div style={{ marginBottom: '30px' }}>
                <h2 style={{ 
                  fontSize: '27px',
                  fontFamily: 'KeplerLightCondensed',
                  marginTop: '2.2rem',
                  marginBottom: '0.5rem',
                  fontWeight: 100
                }}>Ask Yourself</h2>
                {isEditing ? (
                  <textarea
                    value={editedContent?.question || ''}
                    onChange={(e) => updateField('question', e.target.value)}
                    placeholder="What question should you ask yourself?"
                    style={getTextareaStyle('80px')}
                    onFocus={handleTextareaFocusWithExpand}
                    onBlur={handleTextareaBlur}
                    onInput={handleTextareaInput}
                    maxLength={500}
                  />
                ) : (
                  <div className="question">
                    <p>{editedContent.question}</p>
                  </div>
                )}
              </div>
            )}

            {/* Read-only sections */}
            {editedContent?.tarotTip && (
              <div className="tip">
                <h2>Tarot Tip</h2>
                <p>{editedContent.tarotTip}</p>
              </div>
            )}

            {editedContent?.hint && (
              <div className="hint">
                <h2>Hint</h2>
                <p>{editedContent.hint}</p>
              </div>
            )}

            {/* Reflections section - only show here for interpreted entries (manual-only shows it above cards) */}
            {!isManualOnly && (
            <div className="reflections" style={{ marginBottom: '30px' }}>
              <h2>My Reflections</h2>
              {(isEditing || !editedContent?.reflections) ? (
                <textarea
                  value={editedContent?.reflections || ''}
                  onChange={(e) => {
                    updateField('reflections', e.target.value);
                    if (!isEditing) {
                      setIsEditing(true);
                    }
                  }}
                  placeholder="What's jumping out to you?"
                  style={getTextareaStyle('80px')}
                  onFocus={handleTextareaFocusWithExpand}
                  onBlur={handleTextareaBlur}
                  onInput={handleTextareaInput}
                  maxLength={500}
                />
              ) : (
                <div className="overall">
                  <p>{editedContent.reflections}</p>
                </div>
              )}
            </div>
            )}

            {/* Copy button */}
            {!isManualOnly && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}>
              <CopyButton
                buttonText="Copy all"
                copiedText="Copied!"
                trackText="/journal/copy_entry"
                copyContent={`${formatDate(entry.createdAt)}\n\n${editedContent?.query ? `${editedContent.query}\n\n` : ''}${editedContent?.cards?.map((card: any) =>
                  `${card.name}:${card.position ? ` (${card.position})` : ''}\n${card.interpretation || ''}`
                ).join('\n\n') || ''}${editedContent?.overall ? `\n\nOverall: ${editedContent.overall}` : ''}${editedContent?.gist ? `\n\nTakeaway: ${editedContent.gist}` : ''}${editedContent?.question ? `\n\nAsk yourself: ${editedContent.question}` : ''}${editedContent?.tarotTip ? `\n\nTarot tip: ${editedContent.tarotTip}` : ''}${editedContent?.hint ? `\n\nHint: ${editedContent.hint}` : ''}${editedContent?.reflections ? `\n\nMy Reflections: ${editedContent.reflections}` : ''}\n\nhttps://moonlight.world 🌙`}
                style={{
                  padding: '14px 0px 14px 0px',
                  width: '100px',
                  color: dk ? "#fff" : "#000",
                  border: dk ? "1px solid rgba(255,255,255,0.6)" : "1px solid #000",
                  ...(dk && { backgroundColor: "#000" })
                }}
                iconStyle={{
                  ...(dk && { filter: "none" }),
                  width: "18px", 
                  height: "18px",
                  marginRight: "2px",
                  marginTop: "10px"
                }}
              />
            </div>
            )}
          </StyledAnalysis>
        </div>

        {/* Sticky Footer - hide entirely when manual-only on old iOS (no buttons to show) */}
        {!(isManualOnly && !isEditing && isIOSAppVersionAtMost('1.3.2')) && (
        <StyledJournalFooter
          style={{
            padding: isIOSNativeApp() ? '18px 0px 40px 0px' : '12px'
          }}
        >
          {isEditing ? (
            <div style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              animation: `${fadeIn} 0.3s ease forwards`
            }}>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                style={{
                  padding: '8px 20px 8px 20px',
                  margin: '10px 0px',
                  border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                  borderRadius: '12px',
                  backgroundColor: dk ? '#000000' : '#fff',
                  color: dk ? '#fff' : '#000',
                  fontSize: '12px',
                  cursor: 'pointer',
                  width: '80px',
                  height: '34px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = dk ? '#404040' : '#ebebeb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = dk ? '#000000' : '#fff';
                }}
              >
                Cancel
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                disabled={isSaving}
                style={{
                  padding: '8px 20px 8px 20px',
                  margin: '10px 0px',
                  border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                  borderRadius: '12px',
                  backgroundColor: dk ? '#fff' : '#000',
                  color: dk ? '#000' : '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  opacity: isSaving ? 0.6 : 1,
                  width: '80px',
                  height: '34px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSaving) {
                    e.currentTarget.style.backgroundColor = dk ? '#ddd' : '#333';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSaving) {
                    e.currentTarget.style.backgroundColor = dk ? '#fff' : '#000';
                  }
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              gap: '10px',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {isManualOnly && !isIOSAppVersionAtMost('1.3.2') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInterpret();
                  }}
                  disabled={isInterpreting}
                  style={{
                    padding: '10px 14px 10px 14px',
                    margin: '10px 0px',
                    border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                    borderRadius: '12px',
                    backgroundColor: dk ? '#000000' : '#fff',
                    color: dk ? '#fff' : '#000',
                    fontSize: '12px',
                    cursor: isInterpreting ? 'default' : 'pointer',
                    height: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    opacity: isInterpreting ? 0.8 : 1,
                    transition: 'background-color 0.2s ease',
                    ...(isInterpreting && {
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: dk ? '#fff' : '#000'
                    })
                  }}
                  onMouseEnter={(e) => {
                    if (!isInterpreting) {
                      e.currentTarget.style.backgroundColor = dk ? '#404040' : '#ebebeb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isInterpreting) {
                      e.currentTarget.style.backgroundColor = dk ? '#000000' : '#fff';
                    }
                  }}
                >
                  {isInterpreting ? (
                    <>
                      <SimpleLoadingIndicator
                        style={{
                          width: '16px',
                          height: '16px',
                          position: 'static',
                          marginTop: '0'
                        }}
                      />
                      <span>Analyzing...</span>
                    </>
                  ) : (
                    'Help interpret'
                  )}
                </button>
              )}
              {!isManualOnly && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    trackEvent("/journal/edit_entry");
                    setIsEditing(true);

                    // Scroll to date divider when entering edit mode
                    setTimeout(() => {
                      const dateDivider = document.querySelector('.summary-date-divider');
                      if (dateDivider) {
                        (dateDivider as HTMLElement).style.scrollMarginTop = '80px';
                        dateDivider.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        });
                      }
                    }, 100);
                  }}
                  style={{
                    padding: '10px 20px 10px 20px',
                    margin: '10px 0px',
                    border: dk ? '1px solid rgba(255,255,255,0.6)' : '1px solid #000',
                    borderRadius: '12px',
                    backgroundColor: dk ? '#000000' : '#fff',
                    color: dk ? '#fff' : '#000',
                    fontSize: '12px',
                    cursor: 'pointer',
                    gap: '6px',
                    width: '90px',
                    height: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = dk ? '#404040' : '#ebebeb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = dk ? '#000000' : '#fff';
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          )}
        </StyledJournalFooter>
        )}
      </div>
    </StyledSummaryOverlay>
  );
};

// Styled components
const StyledSummaryOverlay = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 999999,
  overflowX: 'hidden',

  '& .summary-container': {
    backgroundColor: 'inherit',
    borderRadius: '24px',
    padding: '32px',
    maxWidth: '460px',
    width: '90%',
    maxHeight: vh(84),
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '6px',
      backgroundColor: 'rgba(127, 127, 127, .5)',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
      marginTop: '24px',
      marginBottom: '24px',
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: 'transparent',
    },
  },

  '& .summary-content': {
    padding: '28px 16px',
    paddingBottom: '120px',
    position: 'relative',
    width: '100%',
    minHeight: 'min-content',
  },

  '@mobile': {
    backgroundColor: 'transparent !important',
    height: '-webkit-fill-available',
    '& .summary-container': {
      border: 'none !important',
      borderRadius: 0,
      width: '100%',
      height: '100%',
      maxHeight: '100%!important',
      minHeight: `calc(${vh(100)} - 140px)`,
      margin: 0,
      paddingTop: '24px!important',
    },
  },

  variants: {
    isIOSNative: {
      true: {
        "@mobile": {
          '& .summary-container': {
            paddingBottom: "180px",
          },
        },
      },
      false: {},
    },
    Theme: {
      light: {
        backgroundColor: 'rgba(180, 180, 180, 0.4)',
        '& .summary-container': {
          backgroundColor: '$wash',
          border: "1px solid $gray600"
        },
      },
      dark: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        '& .summary-container': {
          backgroundColor: '$darkwash',
          color: '#fff',
          border: "1px solid $gray600"
        },
        '& .x-button': {
          filter: 'invert(1)',
        }
      },
    },
  },
});

const StyledAnalysis = styled('div', {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  WebkitTouchCallout: 'none',
  paddingTop: '10px',

  '& img': {
    width: "30%",
    borderRadius: "8px",
    marginBottom: "10px",
    maxWidth: "100px",
    userSelect: "none",
    WebkitUserSelect: "none",
  },
  '& h1': {
    fontSize: '27px',
    fontFamily: 'KeplerLightCondensed',
    marginBottom: '3px',
    marginTop: "10px",
    fontWeight: 100,
    userSelect: "text",
    WebkitUserSelect: "text",
  },
  '& h2': {
    fontSize: '27px',
    fontFamily: 'KeplerLightCondensed',
    marginTop: '2.2rem',
    marginBottom: '0.5rem',
    fontWeight: 100,
    userSelect: "text",
    WebkitUserSelect: "text",
  },
  '& p': {
    fontSize: "13px",
    lineHeight: '1.4',
    margin: '4px auto 4px',
    userSelect: "text",
    WebkitUserSelect: "text",
    WebkitTouchCallout: "default",
  },
  '& .interpretation': {
    marginBottom: "30px"
  },
  '& .overall': {
    backgroundColor: 'rgba(129, 129, 129, 0.1)',
    border: '1px solid $gray500',
    padding: '1.2rem',
    borderRadius: '12px'
  },
  '& .query': {
    marginBottom: '30px',
    marginTop: '-26px',
    background: '#e9e9e9',
    padding: '6px 10px',
    borderRadius: '20px',
    textAlign: 'center',
    '& p': {
      fontSize: '11px',
      opacity: 0.6,
    }
  },
  '& .gist': {
    border: '0px solid $gray500',
    padding: '.6rem',
    borderRadius: '120px',
    textAlign: 'center',
    backgroundColor: '#ededed',
    color: '#000',
    fontSize: '11px',
    fontStyle: 'italic',
    letterSpacing: '0.02em'
  },
  '& .question': {
    fontWeight: "500",
  },
  '& .card-section': {
    display: 'flex',
    gap: '20px',
    marginBottom: '30px',
    alignItems: 'flex-start',
  },
  '& .card-content': {
    flex: 1,
  },

  variants: {
    Theme: {
      dark: {
        '& .overall': {
          backgroundColor: '$gray800'
        },
        '& .query': { background: 'rgb(51, 51, 51)' },
        '& .gist': { backgroundColor: '$subtleFill', color: '#fff' },
        '& textarea::placeholder': { color: 'rgba(255,255,255,0.4)' }
      },
      light: {}
    }
  }
});

const StyledJournalFooter = styled('div', {
  position: 'sticky',
  bottom: '-32px',
  left: '-32px',
  right: '-32px',
  width: 'calc(100% + 64px)',
  height: '120px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderTop: '1px solid $gray400',
  backgroundColor: '$wash',
  padding: '12px', 
  marginTop: 'auto',
  marginBottom: '-32px',
  [`.${darkTheme.className} &`]: { backgroundColor: '$darkwash', borderTopColor: '$gray600' },
});
